import React, { useContext, useState } from "react";
import axios from "axios";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { Button, Card, StatePanel } from "../components/ui";
import { UserContext } from "../contexts/index.jsx";
import { HOME_PATH } from "../core/config/dashboard.js";
import { requireResponse } from "../core/errors/requireResponse";
import { setStorageKey } from "../core/utils/storage";

/**
 * /invite/:token — the invitee joins the store (F3).
 *
 * Never accepts on load: joining a store is the person's decision, so it waits
 * for an explicit click. The backend lets a signed-in person who is not staff
 * yet reach only this accept call (RcWebAccess::inviteeMayCall); accepting
 * seats them, after which the whole app opens up.
 */
const AcceptInvite = () => {
  const { user, update, setActiveStore, logout } = useContext(UserContext);
  const { token } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState("idle");
  const [busy, setBusy] = useState(false);

  const accept = async () => {
    setBusy(true);

    let data;
    try {
      ({ data } = requireResponse(
        await axios.post(`/api/invites/${encodeURIComponent(token)}/accept`)
      ));
    } catch (err) {
      const code = err?.response?.status;

      setStatus(code === 404 ? "gone" : code === 403 ? "mismatch" : code === 401 ? "signed-out" : "error");
      setBusy(false);
      return;
    }

    // Accepted. Re-read the user so the stored copy carries the new seat,
    // approval and admin flag before the reload below. UserContext's refresh()
    // is a mount-time closure that does nothing for someone who signed in
    // during this visit, which is exactly how an invitee gets here. Best
    // effort: the reload re-reads the user anyway.
    try {
      const fresh = await axios.get(`/api/users/${user.id}`, { metadata: { silent: true } });
      if (fresh?.data?.user) update(fresh.data.user);
    } catch (err) {
      // Ignore: the seat exists either way.
    }

    // Navigate first: setActiveStore() reloads the page, and the reload must
    // land on the dashboard, not back on this (now used) invitation.
    history.replace(HOME_PATH);
    setActiveStore(data?.store_id);
  };

  const signOut = () => {
    // Come back here after signing in with the right account.
    setStorageKey("afterLoginRedirect", location.pathname);
    logout();
  };

  if (status === "gone") {
    return (
      <StatePanel
        state="error"
        title="This invitation has expired or was already used"
        description="Ask the store owner to add you again."
      />
    );
  }

  if (status === "mismatch" || status === "signed-out") {
    return (
      <StatePanel
        state="permission"
        title={status === "mismatch" ? "This invitation was sent to someone else" : "Sign in again"}
        description="Sign in with the phone number or email it was sent to."
        action={{ label: "Sign out", onClick: signOut }}
      />
    );
  }

  if (status === "error") {
    return (
      <StatePanel
        state="error"
        description="We couldn’t accept the invitation just now."
        onRetry={() => { setStatus("idle"); accept(); }}
      />
    );
  }

  return (
    <Card padding="lg" className="rc-accept-invite">
      <h2>You've been invited to join a store on Retail Circle</h2>
      <p>Signed in as {user?.phone || user?.email}</p>
      <Button variant="primary" size="lg" onClick={accept} loading={busy} disabled={busy}>
        Join the team
      </Button>
    </Card>
  );
};

export default AcceptInvite;
